import { useLocation, Navigate } from 'react-router-dom';
import ClusterPage, { H2, P, Stat } from '../../../components/cluster/ClusterPage';
import { getClusterCity, CLUSTER_CITIES, VERTICALS, FACTS } from '../../../data/cluster';

export default function LocationSpokePage() {
  const { pathname } = useLocation();
  // path: /chatgpt-ads-<slug>
  const slug = pathname.replace(/^\/chatgpt-ads-/, '').replace(/\/+$/, '');
  const c = slug ? getClusterCity(slug) : undefined;
  if (!c) return <Navigate to="/chatgpt-ads-south-florida-home-services" replace />;

  const kw = `chatgpt ads ${c.name.toLowerCase()}`;
  const otherCities = CLUSTER_CITIES.filter((x) => x.slug !== c.slug);

  return (
    <ClusterPage
      key={c.slug}
      slug={`chatgpt-ads-${c.slug}`}
      keyword={kw}
      videoSrc={`/videos/fame-chatgpt-ads-${c.slug}-roofers.mp4`}
      videoPoster={`/videos/fame-chatgpt-ads-${c.slug}-roofers.jpg`}
      videoName={`ChatGPT Ads in ${c.name} for Home-Service Businesses — FAME explainer`}
      secondaryVideoSrc={`/videos/fame-chatgpt-ads-${c.slug}-hvac-companies.mp4`}
      secondaryVideoPoster={`/videos/fame-chatgpt-ads-${c.slug}-hvac-companies.jpg`}
      secondaryVideoName={`ChatGPT Ads for HVAC companies in ${c.name} — FAME explainer`}
      secondaryVideoDescription={`How FAME runs ZIP-targeted ChatGPT Ads for ${c.name} HVAC companies: context-hint targeting around emergency AC and heating jobs, with conversion tracking on every lead.`}
      secondaryVideoLabel={`60-second explainer: ChatGPT Ads for ${c.name} HVAC companies.`}
      title={`ChatGPT Ads in ${c.name} for Home-Service Businesses`}
      h1={`ChatGPT Ads in ${c.name} for Home-Service Businesses`}
      eyebrow={`${c.county} County · ${c.metro}`}
      leadAnswer={`ChatGPT Ads in ${c.name} let home-service businesses run a sponsored card beneath ChatGPT answers, targeted down to ${c.name} ZIP codes at a $3–5 cost-per-click with no minimum spend. FAME builds the verified account, writes ${c.name} context-hint targeting, and tracks every lead so local contractors win jobs from AI search.`}
      metaDescription={`ChatGPT Ads in ${c.name}, FL: ZIP-targeted sponsored placements in ChatGPT for ${c.county} County home-service contractors — roofing, HVAC, plumbing, electrical, garage door and restoration. Managed by FAME.`}
      keywords={`${kw}, chatgpt advertising ${c.name.toLowerCase()}, openai ads ${c.name.toLowerCase()}, ai marketing agency ${c.name.toLowerCase()}`}
      areaServed={[c.name, `${c.county} County`, 'South Florida']}
      serviceName={`ChatGPT Ads Management in ${c.name}`}
      serviceDescription={`Done-for-you ChatGPT Ads for ${c.name}, FL home-service contractors: ZIP-targeted campaigns, context-hint copywriting, conversion tracking and GEO.`}
      faqs={[
        { q: `Can a ${c.name} business run ChatGPT Ads?`, a: `Yes. ${FACTS.selfServe} ${FACTS.noMinimum} ${FACTS.geo} You can target specific ${c.name} ZIP codes.` },
        { q: `How much do ChatGPT Ads cost in ${c.name}?`, a: `${FACTS.cpc} Budgets are set per campaign, so a ${c.name} contractor can test for a few hundred dollars a week.` },
        { q: `Which trades does FAME run ChatGPT Ads for in ${c.name}?`, a: `Roofing, HVAC, plumbing, electrical, garage door, and fire/water/mold restoration — the six ${c.metro} home-service verticals where one booked job pays for the campaign.` },
        { q: `Will ChatGPT Ads make my ${c.name} business show up inside the answer?`, a: `No — paid ads appear below the answer and do not influence it. To be named inside the answer you need GEO. ${FACTS.trust} We sell both as one "Own the ChatGPT Answer" package.` },
        { q: `How is this billed?`, a: `A monthly management retainer; ad spend is separate and goes straight to OpenAI on your own account. Month-to-month.` },
      ]}
      breadcrumb={[
        { name: 'Home', url: 'https://floridaaimarketingexperts.com/' },
        { name: 'ChatGPT Ads for South Florida Home Services', url: 'https://floridaaimarketingexperts.com/chatgpt-ads-south-florida-home-services' },
        { name: `ChatGPT Ads in ${c.name}`, url: `https://floridaaimarketingexperts.com/chatgpt-ads-${c.slug}` },
      ]}
      upLinks={[
        { label: 'ChatGPT Ads for South Florida Home Services (hub)', to: '/chatgpt-ads-south-florida-home-services' },
        { label: 'How much do ChatGPT Ads cost?', to: '/how-much-do-chatgpt-ads-cost' },
        { label: 'Get recommended by ChatGPT (GEO)', to: '/get-my-business-recommended-by-chatgpt' },
      ]}
      crossLinks={[
        ...VERTICALS.map((v) => ({ label: `ChatGPT Ads for ${v.name}`, to: `/chatgpt-ads-for-${v.slug}-florida` })),
        ...otherCities.map((o) => ({ label: `ChatGPT Ads in ${o.name}`, to: `/chatgpt-ads-${o.slug}` })),
      ]}
    >
      <H2>Why {c.name} contractors should move first</H2>
      <P>
        {c.name} homeowners are asking ChatGPT for help before they open Google. ChatGPT Ads is the first
        channel that lets a {c.county} County contractor buy placement directly in that conversation, and
        almost no local competitor has set it up yet.
      </P>
      <Stat>{FACTS.selfServe} {FACTS.noMinimum}</Stat>

      <H2>ZIP-level targeting built for {c.name}</H2>
      <P>
        The unlock for local is geo-targeting. You no longer waste spend on the whole country — you target
        the {c.name} ZIP codes where your best jobs come from.
      </P>
      <Stat>{FACTS.geo}</Stat>
      <Stat>{FACTS.hints}</Stat>

      <H2>The six trades we run in {c.name}</H2>
      <P>
        We build ChatGPT Ads campaigns for roofing, HVAC, plumbing, electrical, garage door, and
        fire/water/mold restoration businesses across {c.name} and {c.county} County — each with context
        hints written around its highest-margin emergency job.
      </P>
      <Stat>{FACTS.cpc}</Stat>

      <H2>Honest tracking for {c.name} campaigns</H2>
      <P>
        {FACTS.attribution} We instrument client-side events and UTM-tag every URL so your first 30 days in
        {` ${c.name}`} are a measured test, not guesswork.
      </P>
      <Stat>{FACTS.conversionRate}</Stat>
    </ClusterPage>
  );
}
